import { InfiniteScrollSentinel } from "@/components/shared/InfiniteScrollSentinel";
import { useIsTabActive } from "@/contexts/TabActivityContext";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";
import { CollectionCard } from "./CollectionCard";
import type { Collection } from "@/types/collections";
import { LABEL } from "@/lib/typography";

interface CollectionGridProps {
  collections: Collection[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  onCreateNew?: () => void;
  isLoading?: boolean;
  className?: string;
}

/** The collections grid, which Suggested shares: two up, growing to five at desktop. */
export const COLLECTION_GRID_CLASS = "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5";

export function CollectionGrid({
  collections,
  hasMore,
  onLoadMore,
  onCreateNew,
  isLoading,
  className,
}: CollectionGridProps) {
  const active = useIsTabActive();

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className={COLLECTION_GRID_CLASS}>
        {/* Create new card — desktop only (mobile uses header icon) */}
        {onCreateNew && (
          <button
            onClick={onCreateNew}
            className="hidden md:flex h-full min-h-[160px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <Plus className="h-6 w-6" />
            <span className={LABEL}>New Collection</span>
          </button>
        )}

        {collections.map((collection) => (
          <CollectionCard key={collection.collection_id} collection={collection} />
        ))}
      </div>

      {isLoading && !onLoadMore && <p role="status" className="loading-dots py-4 text-center text-ui-body text-muted-foreground">Loading collections</p>}
      {onLoadMore && <InfiniteScrollSentinel enabled={active} hasMore={!!hasMore}
        isLoading={!!isLoading} onLoadMore={onLoadMore}
        cursor={`${collections[0]?.collection_id ?? ""}:${collections.length}`} />}
    </div>
  );
}
